/**
 * Traduz erros técnicos do Twilio Voice SDK / gateway para texto do atendente.
 * Espelha a lógica de humanizeVoiceFailure da API (voiceCallLogService).
 */

export function twilioErrorCode(error) {
  return Number(error?.code || error?.twilioError?.code || 0) || 0;
}

export function twilioErrorRawMessage(error) {
  if (error == null) return "";
  if (typeof error === "string") return error;
  return String(error?.message || error || "");
}

/** JWT/Access Token — o Device precisa de um token novo, não de um rebuild por 31005. */
export function isTwilioAccessTokenError(code, raw = "") {
  const n = Number(code) || 0;
  const blob = `${raw || ""} ${n || ""}`.trim();
  return (
    n === 20104 ||
    n === 31204 ||
    n === 31205 ||
    /20104|31204|31205|token.*(expir|invalid)|access token/i.test(blob)
  );
}

/**
 * Hiccup de sinalização/ICE do Device (WebSocket/gateway HANGUP).
 * Comum no softphone ocioso — não é falha de uma ligação.
 */
export function isTwilioTransientConnectionError(code, raw = "") {
  const n = Number(code) || 0;
  const blob = `${raw || ""} ${n || ""}`.trim();
  if (isTwilioAccessTokenError(n, raw)) return false;
  return (
    n === 31000 ||
    n === 31003 ||
    n === 31005 ||
    n === 31009 ||
    /31000|31003|31005|31009|error sent from gateway|gateway in hangup|connection timed out|no transport is available/i.test(
      blob
    )
  );
}

/**
 * Decide se um Device.error deve abrir aviso na UI.
 * 31005 ocioso / token (reconecta sozinho) ficam de fora.
 */
export function shouldSurfaceTwilioDeviceError(
  error,
  { deviceState, destroyed, isCurrentDevice } = {}
) {
  if (destroyed || isCurrentDevice === false) return false;
  const code = twilioErrorCode(error);
  const raw = twilioErrorRawMessage(error);
  if (isTwilioAccessTokenError(code, raw)) return false;
  if (isTwilioTransientConnectionError(code, raw)) {
    const state = String(deviceState || "").toLowerCase();
    return Boolean(state) && state !== "registered" && state !== "registering";
  }
  return true;
}

const DEVICE_31005 =
  "A conexão do softphone com a telefonia instabilizou (código 31005). Sem ligação em andamento isso costuma se resolver sozinho. Se persistir, recarregue a página.";
const CALL_31005 =
  "A conexão com a telefonia caiu antes de completar a ligação (código 31005). Confira se o número de destino está correto (sem zero de tronco após o 55), se a internet do atendente está estável, e tente de novo.";

export function humanizeVoiceCallError(raw, { status, code, scope } = {}) {
  const s = String(raw || "").trim();
  const codeNum = Number(code || 0) || 0;
  const st = String(status || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");
  const blob = `${s} ${codeNum || ""}`.trim();
  const isDevice = String(scope || "").toLowerCase() === "device";

  if (!s && !codeNum) {
    if (st === "busy") return "A linha estava ocupada.";
    if (st === "canceled" || st === "cancelled") return "A ligação foi cancelada.";
    if (st === "failed") return "A ligação não pôde ser completada.";
    if (st === "client-error" || st === "client_error") {
      return "Não foi possível iniciar a ligação neste navegador.";
    }
    return "";
  }

  if (/unverified|trial accounts may only|21219/i.test(blob)) {
    return "Conta em modo de teste: só é possível ligar para números verificados. Confira o destino na lista de números autorizados ou peça o upgrade da conta.";
  }
  if (/13214|invalid callerid|caller id must be provided/i.test(blob)) {
    return "Falta um número de origem válido no canal de telefonia. Configure o telefone do canal.";
  }
  if (/11200|http retrieval|webhook/i.test(blob)) {
    return "A telefonia não conseguiu obter as instruções da ligação. Tente novamente em instantes.";
  }
  if (
    /13224|21401|21211|invalid.*phone|not a valid phone|número inválido|destino inválido|zero de tronco/i.test(
      blob
    )
  ) {
    return "O número de destino é inválido para discagem. Confira DDD/DDI e remova zeros a mais após o 55 (ex.: +55 11 4395-1891).";
  }
  if (
    codeNum === 31005 ||
    /31005|error sent from gateway|gateway in hangup|connectionerror\s*\(\s*31005\s*\)/i.test(blob)
  ) {
    return isDevice ? DEVICE_31005 : CALL_31005;
  }
  if (codeNum === 31003 || /31003|connection timed out/i.test(blob)) {
    return isDevice
      ? "A conexão do softphone excedeu o tempo de espera. Verifique a internet; se persistir, recarregue a página."
      : "A ligação excedeu o tempo de espera na conexão com a telefonia. Verifique a internet e tente novamente.";
  }
  if (codeNum === 31002 || /31002|connection declined/i.test(blob)) {
    return "A telefonia recusou a conexão. Confira o número de destino e as configurações do canal.";
  }
  if (
    codeNum === 31000 ||
    codeNum === 31009 ||
    /31000|31009|signaling|no transport is available/i.test(blob)
  ) {
    return isDevice
      ? "A sinalização do softphone caiu. Recarregue a página se o telefone não reconectar sozinho."
      : "Falha de sinalização da telefonia no navegador. Atualize a página, confira a conexão e tente de novo.";
  }
  if (
    codeNum === 20104 ||
    codeNum === 31204 ||
    codeNum === 31205 ||
    /20104|31204|31205|token.*(expir|invalid)|access token/i.test(blob)
  ) {
    return "A sessão do softphone expirou. Aguarde a reconexão automática ou atualize a página.";
  }
  if (/connectionerror|twilioerror|hangup|gateway/i.test(blob)) {
    return isDevice
      ? "A conexão do softphone com a telefonia foi interrompida. Aguarde a reconexão ou recarregue a página."
      : "A ligação foi interrompida pela telefonia antes de completar. Confira o número e a conexão, e tente de novo.";
  }
  if (/no-answer|no answer/i.test(s)) return "";
  if (/busy/i.test(s)) return "A linha estava ocupada.";
  if (/cancel/i.test(s)) return "A ligação foi cancelada.";

  return s.slice(0, 400);
}
